/**
 * Translation popovers for the Web Reader's "popover" translation style. The
 * layer sits at the document's top left and scrolls with it, so bubbles move
 * with their words as the page scrolls (moving them after each scroll event
 * lags behind iOS's scrolling). Each scroll still places them again after
 * it, which catches up with words that have moved in the layer since: in fixed
 * or sticky parts of the page (a sticky header), or pushed by a layout shift
 * (a late ad).
 */
import $ from "jquery";
import * as _ from "underscore";

type JQueryElement = JQuery<HTMLElement>;

type PopoverEntry = {
  bubble: JQueryElement;
  span: JQueryElement;
  /** Below its word rather than above, decided as it first shows */
  placeBelow?: boolean;
};

type Config = {
  dismissSpan: (span: JQueryElement) => void;
  getPopoverKey: (span: JQueryElement) => string;
  getTranslatedSpans: () => JQuery<HTMLElement>;
  isPopoverStyle: () => boolean;
};

let config: Config | null = null;
let popoverLayer: JQueryElement | null = null;
let popoverAnimationFrame: number | null = null;
/** Whether the scheduled frame also refreshes bubbles from their words */
let refreshBubblesInFrame = false;
let popoverListenersBound = false;
let popoverEntries: { [key: string]: PopoverEntry } = {};

const ensurePopoverLayer = () => {
  if (popoverLayer?.length) {
    return popoverLayer;
  }

  popoverLayer = $("#readerPopoverLayer");
  if (popoverLayer.length === 0) {
    popoverLayer = $(
      '<div id="readerPopoverLayer" class="readerPopoverLayer"/>'
    );
    $("body").append(popoverLayer);
  }

  return popoverLayer;
};

const removePopoverEntry = (key: string) => {
  const entry = popoverEntries[key];
  if (!entry) {
    return;
  }

  entry.bubble.remove();
  delete popoverEntries[key];
};

/** Between a bubble and its word */
const popoverOffset = 6;

type Rect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
  width: number;
  height: number;
};

/**
 * @param topBoundary - viewport y below which the bubble would be clipped by
 *   the popover layer (the bottom of the media player in scroll mode, 0
 *   otherwise). A word just under it has room above in the window but not in
 *   the layer, so its bubble has to go below the word instead.
 * @param placeBelow - where the bubble already is, to keep it there
 */
export const computePopoverPosition = (
  spanRect: Rect,
  clientRects: ArrayLike<Rect>,
  bubbleRect: Pick<Rect, "width" | "height">,
  windowWidth: number,
  topBoundary = 0,
  placeBelow = spanRect.top - bubbleRect.height - popoverOffset < topBoundary
): { left: number; top: number; arrowLeft: number; placeBelow: boolean } => {
  const margin = 8;
  const arrowMinMargin = 12;

  const targetRect =
    clientRects.length <= 1
      ? spanRect
      : placeBelow
        ? clientRects[clientRects.length - 1]
        : clientRects[0];

  const targetCenter = targetRect.left + targetRect.width / 2;
  let left = targetCenter - bubbleRect.width / 2;
  left = Math.max(margin, left);
  left = Math.min(windowWidth - bubbleRect.width - margin, left);

  const top = placeBelow
    ? targetRect.bottom + popoverOffset
    : targetRect.top - bubbleRect.height - popoverOffset;

  const arrowLeft = Math.max(
    arrowMinMargin,
    Math.min(targetCenter - left, bubbleRect.width - arrowMinMargin)
  );

  return { left, top, arrowLeft, placeBelow };
};

const repositionPopoverForKey = (key: string) => {
  const entry = popoverEntries[key];
  if (!entry) {
    return;
  }

  const spanElement = entry.span?.[0];
  if (
    !spanElement ||
    !document.body.contains(spanElement) ||
    !entry.span.hasClass("translated")
  ) {
    removePopoverEntry(key);
    return;
  }

  const spanRect = spanElement.getBoundingClientRect();
  const layerRect = ensurePopoverLayer()[0].getBoundingClientRect();

  // Hidden while its word is scrolled out of the embedded reader's text
  const viewport = document.getElementById("pageTextViewport");
  const viewportRect = viewport?.getBoundingClientRect();
  if (
    viewportRect &&
    (spanRect.bottom < viewportRect.top ||
      spanRect.top > viewportRect.bottom ||
      spanRect.right < viewportRect.left ||
      spanRect.left > viewportRect.right)
  ) {
    entry.bubble.css("display", "none");
    return;
  }

  const bubble = entry.bubble;
  bubble.css({
    display: "block",
    left: "0px",
    top: "0px",
    visibility: "hidden",
  });

  const bubbleRect = bubble[0].getBoundingClientRect();
  const { left, top, arrowLeft, placeBelow } = computePopoverPosition(
    spanRect,
    spanElement.getClientRects(),
    bubbleRect,
    window.innerWidth,
    viewportRect?.top ?? 0,
    entry.placeBelow
  );
  entry.placeBelow = placeBelow;

  bubble[0].style.setProperty("--arrow-left", arrowLeft + "px");
  bubble.toggleClass("popoverBelow", placeBelow);
  bubble.css({
    left: left - layerRect.left + "px",
    top: top - layerRect.top + "px",
    visibility: "visible",
  });
};

const repositionOnScroll = () => schedulePopoverFrame(false);

const bindPopoverListeners = () => {
  if (popoverListenersBound) {
    return;
  }

  $(window).on("resize.spanTranslatorPopover", scheduleRepositionPopovers);
  // Native listener rather than jQuery so we can use the capture phase: the
  // embedded Web Reader scrolls an inner container (#pageTextViewport), and
  // scroll events from it don't bubble up to window.
  window.addEventListener("scroll", repositionOnScroll, true);
  $(document).on(
    "click.spanTranslatorPopover",
    ".readerPopoverBubble",
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      const key = $(event.currentTarget).attr("data-popover-key");
      if (!key) {
        return;
      }

      const entry = popoverEntries[key];
      if (!entry?.span?.length) {
        removePopoverEntry(key);
        return;
      }

      config?.dismissSpan(entry.span);
    }
  );
  popoverListenersBound = true;
};

export const setupPopoverLayer = (nextConfig: Config) => {
  config = nextConfig;
  popoverEntries = {};
  popoverLayer = $("#readerPopoverLayer");
  popoverLayer.empty();
  bindPopoverListeners();
};

export const removePopoverForSpan = (span: JQueryElement) => {
  if (!config) {
    return;
  }

  removePopoverEntry(config.getPopoverKey(span));
};

export const clearPopoverEntries = () => {
  _.each(_.keys(popoverEntries), (key) => {
    removePopoverEntry(key);
  });
};

export const showPopoverForSpan = (
  span: JQueryElement,
  schedulePosition = true
) => {
  if (!config) {
    return;
  }

  if (!config.isPopoverStyle()) {
    removePopoverForSpan(span);
    return;
  }

  const translationElement = span.find(".translation");
  if (translationElement.length === 0) {
    removePopoverForSpan(span);
    return;
  }

  const key = config.getPopoverKey(span);
  const layer = ensurePopoverLayer();
  let entry = popoverEntries[key];
  if (!entry) {
    const bubble = $('<div class="readerPopoverBubble"/>');
    layer.append(bubble);
    entry = {
      bubble,
      span,
    };
    popoverEntries[key] = entry;
  } else {
    entry.span = span;
  }

  entry.bubble.attr("data-popover-key", key);
  const translationStyles = window.getComputedStyle(translationElement[0]);
  entry.bubble.html(translationElement.html());
  entry.bubble.css({
    fontFamily: translationStyles.fontFamily,
    fontSize: translationStyles.fontSize,
    fontWeight: translationStyles.fontWeight,
    lineHeight: translationStyles.lineHeight,
  });
  if (schedulePosition) {
    scheduleRepositionPopovers();
  }
};

export const strikeThroughPopoverForSpan = (span: JQueryElement) => {
  if (!config) {
    return;
  }

  const entry = popoverEntries[config.getPopoverKey(span)];
  if (!entry) {
    return;
  }

  entry.bubble.css("text-decoration", "line-through");
};

const schedulePopoverFrame = (refreshBubbles: boolean) => {
  if (!config) {
    return;
  }

  refreshBubblesInFrame ||= refreshBubbles;
  if (popoverAnimationFrame !== null) {
    return;
  }

  popoverAnimationFrame = window.requestAnimationFrame(() => {
    popoverAnimationFrame = null;
    const refresh = refreshBubblesInFrame;
    refreshBubblesInFrame = false;

    if (!config?.isPopoverStyle()) {
      clearPopoverEntries();
      return;
    }

    if (refresh) {
      config.getTranslatedSpans().each((_index, element) => {
        showPopoverForSpan($(element), false);
      });
    }

    for (const key of Object.keys(popoverEntries)) {
      repositionPopoverForKey(key);
    }
  });
};

export const scheduleRepositionPopovers = () => schedulePopoverFrame(true);

export const closePopoverLayer = () => {
  clearPopoverEntries();
  popoverLayer?.remove();
  popoverLayer = null;
  if (popoverAnimationFrame !== null) {
    cancelAnimationFrame(popoverAnimationFrame);
    popoverAnimationFrame = null;
  }
  $(window).off("resize.spanTranslatorPopover");
  window.removeEventListener("scroll", repositionOnScroll, true);
  $(document).off("click.spanTranslatorPopover");
  popoverListenersBound = false;
};
